/**
 * Dedicated client for the headless impersonation bridge.
 *
 * Deliberately bypasses the shared `ApiClient` and `ensureGuestToken`:
 *
 * 1. `Bridge` must be sent WITHOUT an `Authorization` header — the one-time code
 *    in the URL is the sole credential, and any attached token would change which
 *    customer `JwtClaimsMiddleware` attaches before the endpoint runs.
 * 2. `Finish` must be sent WITH the impersonation token, and must never trigger
 *    the shared 401 handler: its expired-authenticated-user path redirects to
 *    `/login`, which would hijack the admin mid-impersonation and break the
 *    "return to admin area" flow. Failures surface in the banner instead.
 *
 * Backend endpoints live on the marketplace plugin's api-bf surface.
 */

const IMPERSONATION_BASE_URL = 'https://' + import.meta.env.VITE_BACKEND_CUSTOM_DOMAIN_NAME + '/api-bf/';

export interface ImpersonationBridgeRequest {
  code: string;
}

export interface ImpersonationBridgeResponse {
  token: string;
  expires_in_seconds: number;
  target_customer_id: number;
  target_display_name: string;
  target_email: string;
  /**
   * Absolute base URL of the admin area as observed by the nop Start endpoint,
   * carried through from the signed bridge payload. Persisted in the
   * impersonation session so Finish / Return-to-admin can send the admin back to
   * the exact host they started from (e.g. the *.azurewebsites.net host).
   */
  admin_base_url: string | null;
}

export interface ImpersonationFinishResponse {
  admin_id: number;
  admin_base_url: string | null;
}

export class ImpersonationError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ImpersonationError';
    this.status = status;
  }
}

const extractError = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    if (body && typeof body === 'object') {
      const obj = body as Record<string, unknown>;
      if (typeof obj.message === 'string') return obj.message;
      if (typeof obj.error === 'string') return obj.error;
    }
    if (typeof body === 'string' && body.length > 0) return body;
  } catch {
    // fall through
  }
  return `Impersonation request failed (HTTP ${response.status})`;
};

/**
 * Exchanges the bridge code for the impersonation token. No auth header is sent.
 */
export const bridgeImpersonation = async (
  code: string,
): Promise<ImpersonationBridgeResponse> => {
  const response = await fetch(`${IMPERSONATION_BASE_URL}Impersonation/Bridge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code } satisfies ImpersonationBridgeRequest),
  });

  if (!response.ok) {
    throw new ImpersonationError(await extractError(response), response.status);
  }
  return (await response.json()) as ImpersonationBridgeResponse;
};

/**
 * Ends the impersonation session. Sends the impersonation token; never redirects
 * on failure — callers show the error in the banner.
 */
export const finishImpersonation = async (
  token: string,
): Promise<ImpersonationFinishResponse> => {
  const response = await fetch(`${IMPERSONATION_BASE_URL}Impersonation/Finish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
  });

  if (!response.ok) {
    throw new ImpersonationError(await extractError(response), response.status);
  }
  return (await response.json()) as ImpersonationFinishResponse;
};

/**
 * Reads the impersonation token from the "auth_token" slot used by the shared
 * ApiClient, without importing the utils module (avoids pulling store deps into
 * pages that must not dispatch loginExpired etc.). Returns null when absent.
 */
export const getImpersonationToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('auth_token');
};